// src/common/js/getCurrentLocation.js
import { initAMapKey } from "@/common/utility/utils";
import {kpiGetAddress} from "@/api";
import store from "@/store";

export class LocationService {
  /**
   * 获取当前地理位置
   * @returns {Promise<Object>} 位置信息对象
   */
  static async getCurrentPositionWithBrowser(options) {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('浏览器不支持地理位置功能'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          resolve({
            latitude,
            longitude,
            accuracy,
            lng: longitude,
            lat: latitude,
            timestamp: position.timestamp
          });
        },
        (error) => {
          reject(LocationService.handleLocationError(error));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 3000,
          offset: [10, 20],
          ...(options || {})
        }
      );
    });
  }

  /**
   * 处理位置获取错误
   * @param {Object} error 错误对象
   * @returns {Error} 格式化后的错误
   */
  static handleLocationError(error) {
    console.log(error)
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return new Error('用户拒绝了地理位置请求');
      case error.POSITION_UNAVAILABLE:
        return new Error('位置信息不可用');
      case error.TIMEOUT:
        return new Error('获取位置信息超时');
      default:
        return new Error('获取位置时发生未知错误');
    }
  }

  /**
   * 获取当前地理位置坐标 (高德地图API)
   * @param {Object} options 定位选项
   * @returns {Promise<Object>} 包含经纬度的Promise
   */
  static getCurrentPositionWithAMap(options = {}) {
    // 不在 Promise 执行器中使用 async，而是返回一个 Promise
    return new Promise((resolve, reject) => {
      if (!window.AMap) {
        reject(new Error('AMap not loaded'));
        return;
      }

      window.AMap.plugin('AMap.Geolocation', () => {
        const geolocation = new window.AMap.Geolocation({
          enableHighAccuracy: true,
          timeout: options.timeout || 3000,
          buttonOffset: new window.AMap.Pixel(10, 20),
          ...options
        });

        geolocation.getCurrentPosition((status, result) => {
          if (status === 'complete' && result.info === 'SUCCESS') {
            resolve({
              lng: result.position.lng,
              lat: result.position.lat,
              position: result.position,
              result: result
            });
          } else {
            reject(new Error(result.message || '定位失败'));
          }
        });
      });
    });
  }

  /**
   * 根据经纬度获取详细地址信息
   * @param {Object} position 经纬度信息
   * @param {Number} MapSource 坐标信息 0 WGS84 1 GCJ02
   * @param {Object} CityDataList 匹配的城市数据
   * @returns {Promise<Object>} 地址信息Promise
   */
  static async getAddressByLngLat(position, MapSource, CityDataList) {
    let res = await kpiGetAddress({
      Longitude: position.lng,
      Latitude: position.lat,
      // 高德 写死 2 其他写死 0
      CoordinateSystem: MapSource === 1 ? 2 : 0, // 0 WGS84 1 GCJ02
      UserAgent: window.navigator.userAgent.toLowerCase(),
    })
    console.log(res)
    let data = res.ResultData
    let city = LocationService.matchCityByName(data.CityName, CityDataList)
    if (city) {
      return {
        DetailAddress: data.DetailAddress,
        DistrictName: data.DistrictName,
        ShortAddress: data.ShortAddress,
        lng: position.lng,
        lat: position.lat,
        ...city
      }
    } else {
      throw new Error('匹配城市数据失败，位置信息不可用');
    }
  }

  static matchCityByName(cityName, cityDataList) {
    if (!cityName || !cityDataList || !cityDataList.list || cityDataList.list.length === 0) {
      return null
    }

    let formattedCityName = cityName
    if (formattedCityName.endsWith('市')) {
      formattedCityName = formattedCityName.substring(0, formattedCityName.length - 1)
    }

    for (const element of cityDataList.list) {
      if (element.CityList && Array.isArray(element.CityList)) {
        for (const city of element.CityList) {
          if (city.CityName_CN === formattedCityName) {
            return city
          }
        }
      }
    }

    return null
  }

  /**
   * 获取当前位置详细信息（坐标+地址）
   * @param {Object} options 定位选项
   * @param {Object} CityDataList 匹配的城市数据
   * @returns {Promise<Object>} 位置详细信息
   */
  static async getCurrentPosition(options = {}, CityDataList) {
    let position = ''
    let MapSource = ''
    if (store.state.AMapKey && store.state.AMapKey.MapSource === 1) {
      await initAMapKey();
      position = await LocationService.getCurrentPositionWithAMap(options)
      MapSource = 1
    } else {
      position = await LocationService.getCurrentPositionWithBrowser(options)
      MapSource = 0
    }
    if (position) {
      let CityItem = await LocationService.getAddressByLngLat(position, MapSource, CityDataList)
      if (CityItem && CityItem.CityCode) {
        if (options.serviceType === 'getLngAndLat') {
          return {...position, MapSource};
        } else {
          return {...CityItem, MapSource}
        }
      }
    }
  }
}
